import React from "react";
import { Box, Typography, useTheme, alpha } from "@mui/material";
import Chart from "react-apexcharts";

interface GaugeChartProps {
  value: number;
  title: string;
  subtitle?: string;
  min?: number;
  max?: number;
  height?: number;
  color?: string;
  showValue?: boolean;
}

export const GaugeChart: React.FC<GaugeChartProps> = ({
  value,
  title,
  subtitle,
  min = 0,
  max = 100,
  height = 316,
  color,
  showValue = true,
}) => {
  const theme = useTheme();

  const getHealthColor = (score: number) => {
    if (score >= 80) return theme.palette.success.main;
    if (score >= 60) return theme.palette.warning.main;
    if (score >= 40) return theme.palette.error.main;
    return theme.palette.grey[500];
  };

  const chartColor = color || getHealthColor(value);

  const options = {
    chart: {
      type: "radialBar" as const,
      toolbar: {
        show: false,
      },
      sparkline: {
        enabled: false,
      },
    },
    plotOptions: {
      radialBar: {
        startAngle: -90,
        endAngle: 90,
        hollow: {
          margin: 20,
          size: "75%",
          background: "transparent",
        },
        track: {
          background:
            theme.palette.mode === "dark"
              ? theme.palette.grey[700]
              : theme.palette.grey[300],
          strokeWidth: "8px",
          margin: 0,
          opacity: 1,
          dropShadow: {
            enabled: false,
          },
        },
        dataLabels: {
          name: {
            show: false,
          },
          value: {
            show: false,
          },
        },
      },
    },
    fill: {
      type: "solid",
      colors: [chartColor],
    },
    stroke: {
      lineCap: "round" as const,
      width: 8,
    },
    colors: [chartColor],
    labels: ["Score"],
  };

  const series = [((value - min) / (max - min)) * 100];

  return (
    <Box
      sx={{
        height: height,
        bgcolor: "background.paper",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 2,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        cursor: "pointer",
        transition: "all 0.3s ease",
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 1px 3px rgba(0,0,0,0.3)"
            : "0 1px 3px rgba(0,0,0,0.1)",
        "&:hover": {
          borderColor: chartColor,
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 4px 12px rgba(0,0,0,0.4), 0 0 0 1px ${alpha(
                  chartColor,
                  0.2
                )}`
              : `0 4px 12px rgba(0,0,0,0.15), 0 0 0 1px ${alpha(
                  chartColor,
                  0.2
                )}`,
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Top accent line */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          bgcolor: chartColor,
        }}
      />

      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 1, position: "relative", zIndex: 1 }}>
        <Typography
          variant="h5"
          sx={{
            color: "text.primary",
            fontWeight: 600,
            mb: 0.5,
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: "0.875rem",
              fontWeight: 400,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Gauge Chart Container */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          zIndex: 1,
          minHeight: 180,
        }}
      >
        <Chart
          options={options}
          series={series}
          type="radialBar"
          height={250}
          width="100%"
        />

        {/* Center Value Display */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -15%)",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 500,
              color: chartColor,
              lineHeight: 1,
              fontSize: { xs: "2rem", sm: "2.25rem" },
              mb: 0.5,
            }}
          >
            {Math.round(value)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: "0.75rem",
              fontWeight: 400,
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            Health Score
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
